import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Collapse,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Popover,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import { getJwtPayload } from '../utils/jwt';

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminPage() {
  const token = useMemo(() => localStorage.getItem('token'), []);
  const payload = useMemo(() => getJwtPayload(token), [token]);
  const isAdmin = payload?.role === 'admin';

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [users, setUsers] = useState([]);
  const [usersWithPermissions, setUsersWithPermissions] = useState([]);
  const [devicesByTable, setDevicesByTable] = useState({});

  // États pour sélection multiple
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedTable, setSelectedTable] = useState('climatrack');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);

  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    permissions: true,
    users: true,
  });
  const [permissionsAnchorEl, setPermissionsAnchorEl] = useState(null);
  const [permissionsUser, setPermissionsUser] = useState(null);


  useEffect(() => {
    if (!isAdmin) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function loadData() {
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const [usersRes, devicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { headers }),
        axios.get(`${API_URL}/api/admin/devices`, { headers }),
      ]);

      setUsers(usersRes.data);
      setDevicesByTable(devicesRes.data);

      const usersWithPerms = await Promise.all(
        usersRes.data.map(async (user) => {
          if (user.role === 'admin') {
            return { ...user, permissions: null };
          } else {
            try {
              const permRes = await axios.get(
                `${API_URL}/api/admin/users/${user.id}/permissions`,
                { headers }
              );
              return { ...user, permissions: permRes.data };
            } catch (e) {
              return { ...user, permissions: [] };
            }
          }
        })
      );

      setUsersWithPermissions(usersWithPerms);
    } catch (e) {
      const code = e?.response?.status;
      const msg = e?.response?.data?.error || e.message;
      setStatus({ type: 'error', message: `Erreur de chargement: ${code || ''} ${msg}` });
    } finally {
      setLoading(false);
    }
  }

  function getUserPermissionsForTable(userId, tableName) {
    const user = usersWithPermissions.find((u) => u.id === userId);
    if (!user || user.role !== 'user' || !Array.isArray(user.permissions)) return [];
    return user.permissions
      .filter((p) => p.table_name === tableName)
      .map((p) => String(p.device_id));
  }

  function getAssignablePairs() {
    const pairs = [];
    for (const userId of selectedUserIds) {
      const existing = new Set(getUserPermissionsForTable(userId, selectedTable));
      for (const deviceId of selectedDeviceIds) {
        if (!existing.has(String(deviceId))) {
          pairs.push({ userId, deviceId: String(deviceId) });
        }
      }
    }
    return pairs;
  }

  function getRemovablePairs() {
    const pairs = [];
    for (const userId of selectedUserIds) {
      const existing = new Set(getUserPermissionsForTable(userId, selectedTable));
      for (const deviceId of selectedDeviceIds) {
        if (existing.has(String(deviceId))) {
          pairs.push({ userId, deviceId: String(deviceId) });
        }
      }
    }
    return pairs;
  }

  async function handleBulkAssign() {
    setStatus({ type: '', message: '' });

    if (selectedUserIds.length === 0) {
      setStatus({ type: 'warning', message: 'Sélectionnez au moins un utilisateur.' });
      return;
    }

    if (selectedDeviceIds.length === 0) {
      setStatus({ type: 'warning', message: 'Sélectionnez au moins un boîtier.' });
      return;
    }

    const assignablePairs = getAssignablePairs();
    if (assignablePairs.length === 0) {
      setStatus({ type: 'warning', message: 'Tout est déjà assigné.' });
      return;
    }

    setLoading(true);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const { userId, deviceId } of assignablePairs) {
          try {
            await axios.post(
              `${API_URL}/api/admin/permissions`,
              {
                userId: Number(userId),
                tableName: selectedTable,
                deviceId: String(deviceId),
              },
              { headers }
            );
            successCount++;
          } catch (e) {
            errorCount++;
            console.error(`Erreur pour user ${userId}, device ${deviceId}:`, e);
          }
      }

      if (errorCount === 0) {
        setStatus({
          type: 'success',
          message: `${successCount} assignation(s) réussie(s).`,
        });
      } else {
        setStatus({
          type: 'warning',
          message: `${successCount} réussie(s), ${errorCount} échouée(s).`,
        });
      }

      // Réinitialiser les sélections
      setSelectedUserIds([]);
      setSelectedDeviceIds([]);

      // Recharger les données
      await loadData();
    } catch (e) {
      setStatus({ type: 'error', message: `Erreur: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }

  async function handleBulkRemove() {
    setStatus({ type: '', message: '' });

    if (selectedUserIds.length === 0) {
      setStatus({ type: 'warning', message: 'Sélectionnez au moins un utilisateur.' });
      return;
    }

    if (selectedDeviceIds.length === 0) {
      setStatus({ type: 'warning', message: 'Sélectionnez au moins un boîtier.' });
      return;
    }

    const removablePairs = getRemovablePairs();
    if (removablePairs.length === 0) {
      setStatus({ type: 'warning', message: 'Aucune permission à retirer.' });
      return;
    }

    setLoading(true);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const { userId, deviceId } of removablePairs) {
          try {
            await axios.delete(`${API_URL}/api/admin/permissions`, {
              headers,
              data: { userId: Number(userId), tableName: selectedTable, deviceId: String(deviceId) },
            });
            successCount++;
          } catch (e) {
            errorCount++;
            console.error(`Erreur pour user ${userId}, device ${deviceId}:`, e);
          }
      }

      if (errorCount === 0) {
        setStatus({
          type: 'success',
          message: `${successCount} permission(s) supprimée(s).`,
        });
      } else {
        setStatus({
          type: 'warning',
          message: `${successCount} réussie(s), ${errorCount} échouée(s).`,
        });
      }

      // Réinitialiser les sélections
      setSelectedUserIds([]);
      setSelectedDeviceIds([]);

      // Recharger les données
      await loadData();
    } catch (e) {
      setStatus({ type: 'error', message: `Erreur: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }

  function toggleSection(section) {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  function handleUserToggle(userId) {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  }

  function handleDeviceToggle(deviceId) {
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    );
  }

  function handleSelectAllUsers() {
    const normalUserIds = users.filter((u) => u.role === 'user').map((u) => u.id);
    setSelectedUserIds(normalUserIds);
  }

  function handleDeselectAllUsers() {
    setSelectedUserIds([]);
  }

  function handleSelectAllDevices() {
    const allDevices = devicesByTable[selectedTable] || [];
    setSelectedDeviceIds(allDevices);
  }

  function handleDeselectAllDevices() {
    setSelectedDeviceIds([]);
  }

  function handleOpenPermissions(event, user) {
    setPermissionsAnchorEl(event.currentTarget);
    setPermissionsUser(user);
  }

  function handleClosePermissions() {
    setPermissionsAnchorEl(null);
    setPermissionsUser(null);
  }

  const permissionsOpen = Boolean(permissionsAnchorEl);

  async function handleDeletePermission(userId, tableName, deviceId) {
    try {
      await axios.delete(`${API_URL}/api/admin/permissions`, {
        headers,
        data: { userId, tableName, deviceId },
      });

      setStatus({ type: 'success', message: 'Permission supprimée.' });
      await loadData();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      setStatus({ type: 'error', message: `Erreur: ${msg}` });
    }
  }

  const deviceList = devicesByTable?.[selectedTable] || [];
  const filteredUsers = usersWithPermissions.filter(
    (u) =>
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const adminUsers = filteredUsers.filter((u) => u.role === 'admin');
  const normalUsers = filteredUsers.filter((u) => u.role === 'user');
  const normalUsersAll = users.filter((u) => u.role === 'user');

  const assignableCount = useMemo(() => getAssignablePairs().length, [
    selectedUserIds,
    selectedDeviceIds,
    usersWithPermissions,
    selectedTable,
  ]);

  const removableCount = useMemo(() => getRemovablePairs().length, [
    selectedUserIds,
    selectedDeviceIds,
    usersWithPermissions,
    selectedTable,
  ]);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Accès interdit (admin requis).
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        color: '#fff',
        width: '100%',
        maxWidth: { xs: '100%', md: 1200 },
        mx: 'auto',
        boxSizing: 'border-box',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: { xs: 1.5, md: 2 },
          fontSize: { xs: '1.4rem', sm: '1.6rem', md: '2rem' },
        }}
      >
        Administration
      </Typography>

      {status.message && (
        <Alert
          severity={status.type || 'info'}
          onClose={() => setStatus({ type: '', message: '' })}
          sx={{
            mb: 3,
            borderRadius: 2,
            backgroundColor: 'rgba(0,0,0,0.25)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.10)',
            '.MuiAlert-icon': { color: 'rgba(255,255,255,0.75)' },
          }}
        >
          {status.message}
        </Alert>
      )}

      {/* Section 1 : Gestion des permissions */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          borderRadius: { xs: '12px', md: '16px' },
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid rgba(255, 255, 255, 0.10)',
          backdropFilter: 'blur(8px)',
          color: '#fff',
          mb: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 2 }}
          onClick={() => toggleSection('permissions')}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Gestion des permissions
          </Typography>
          {expandedSections.permissions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        <Collapse in={expandedSections.permissions}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: { xs: 1.5, md: 2 } }}>
            Sélectionnez les utilisateurs et les boîtiers.
          </Typography>

          <Paper
            sx={{
              p: { xs: 1.2, md: 2 },
              mb: { xs: 1, md: 2 },
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: 2,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.75)', mb: 1 }}>
              Utilisateurs sélectionnés
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedUserIds.length === 0 ? (
                <Chip label="Aucun" size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
              ) : (
                selectedUserIds.map((id) => {
                  const u = users.find((user) => user.id === id);
                  const label = u ? `${u.first_name} ${u.last_name}` : `User ${id}`;
                  return (
                    <Chip
                      key={id}
                      label={label}
                      size="small"
                      onDelete={() => handleUserToggle(id)}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: '#fff',
                        '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' },
                      }}
                    />
                  );
                })
              )}
            </Box>
          </Paper>

          <Paper
            sx={{
              p: { xs: 1.2, md: 2 },
              mb: { xs: 1, md: 2 },
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: 2,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.75)', mb: 1 }}>
              Boîtiers sélectionnés ({selectedTable})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedDeviceIds.length === 0 ? (
                <Chip label="Aucun" size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
              ) : (
                selectedDeviceIds.map((deviceId) => (
                  <Chip
                    key={deviceId}
                    label={deviceId}
                    size="small"
                    onDelete={() => handleDeviceToggle(deviceId)}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: '#fff',
                      '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' },
                    }}
                  />
                ))
              )}
            </Box>
          </Paper>

          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: { xs: 1.5, md: 2 },
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Utilisateurs ({selectedUserIds.length}/{normalUsersAll.length})
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          normalUsersAll.length > 0 &&
                          selectedUserIds.length === normalUsersAll.length
                        }
                        indeterminate={
                          selectedUserIds.length > 0 &&
                          selectedUserIds.length < normalUsersAll.length
                        }
                        onChange={(e) =>
                          e.target.checked ? handleSelectAllUsers() : handleDeselectAllUsers()
                        }
                        sx={{
                          color: 'rgba(255,255,255,0.5)',
                          '&.Mui-checked': { color: '#fff' },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                        Tout sélectionner
                      </Typography>
                    }
                  />
                </Box>
                <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ maxHeight: { xs: 160, sm: 260, md: 300 }, overflowY: 'auto' }}>
                  <FormGroup>
                    {normalUsersAll.map((user) => (
                      <FormControlLabel
                        key={user.id}
                        control={
                          <Checkbox
                            checked={selectedUserIds.includes(user.id)}
                            onChange={() => handleUserToggle(user.id)}
                            sx={{
                              color: 'rgba(255,255,255,0.5)',
                              '&.Mui-checked': { color: '#4CAF50' },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ color: '#fff' }}>
                            {user.first_name} {user.last_name}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={2}>
              <Paper
                sx={{
                  p: { xs: 1.5, md: 2 },
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Type
                  </Typography>
                  <FormControlLabel
                    control={<Checkbox sx={{ visibility: 'hidden' }} />}
                    label={
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', visibility: 'hidden' }}>
                        Tout sélectionner
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
                <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedTable}
                    onChange={(e) => {
                      setSelectedTable(e.target.value);
                      setSelectedDeviceIds([]);
                    }}
                    sx={{
                      color: '#fff',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.20)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.45)' },
                      '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.75)' },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: { backgroundColor: '#1a1b2e', color: '#fff', border: '1px solid rgba(255,255,255,0.12)' },
                      },
                    }}
                  >
                    <MenuItem value="aquacheck">Aquacheck</MenuItem>
                    <MenuItem value="climatrack">Climatrack</MenuItem>
                    <MenuItem value="greenwatch">Greenwatch</MenuItem>
                  </Select>
                </FormControl>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: { xs: 1.5, md: 2 },
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Boîtiers ({selectedDeviceIds.length}/{deviceList.length})
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          deviceList.length > 0 &&
                          selectedDeviceIds.length === deviceList.length
                        }
                        indeterminate={
                          selectedDeviceIds.length > 0 &&
                          selectedDeviceIds.length < deviceList.length
                        }
                        onChange={(e) =>
                          e.target.checked ? handleSelectAllDevices() : handleDeselectAllDevices()
                        }
                        sx={{
                          color: 'rgba(255,255,255,0.5)',
                          '&.Mui-checked': { color: '#fff' },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                        Tout sélectionner
                      </Typography>
                    }
                  />
                </Box>
                <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ maxHeight: { xs: 160, sm: 260, md: 300 }, overflowY: 'auto' }}>
                  <FormGroup>
                    {deviceList.length === 0 ? (
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                        Aucun boîtier disponible
                      </Typography>
                    ) : (
                      deviceList.map((deviceId) => (
                        <FormControlLabel
                          key={deviceId}
                          control={
                            <Checkbox
                              checked={selectedDeviceIds.includes(deviceId)}
                              onChange={() => handleDeviceToggle(deviceId)}
                              sx={{
                                color: 'rgba(255,255,255,0.5)',
                                '&.Mui-checked': { color: '#4CAF50' },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: '#fff' }}>
                              {deviceId}
                            </Typography>
                          }
                        />
                      ))
                    )}
                  </FormGroup>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              justifyContent: 'center',
              mt: { xs: 1.5, md: 3 },
              mb: { xs: 1, md: 0 },
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              onClick={handleBulkAssign}
              disabled={loading || assignableCount === 0}
              sx={{
                borderRadius: '12px',
                px: { xs: 2.5, md: 4 },
                py: { xs: 1.1, md: 1.5 },
                fontWeight: 700,
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.15)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.3)',
                },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Assigner ({assignableCount})
            </Button>
            <Button
              variant="outlined"
              onClick={handleBulkRemove}
              disabled={loading || removableCount === 0}
              sx={{
                borderRadius: '12px',
                px: { xs: 2.5, md: 4 },
                py: { xs: 1.1, md: 1.5 },
                fontWeight: 700,
                textTransform: 'none',
                borderColor: 'rgba(255,255,255,0.35)',
                color: '#fff',
                '&:hover': { borderColor: 'rgba(255,255,255,0.6)', backgroundColor: 'rgba(255,255,255,0.08)' },
                '&.Mui-disabled': {
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.3)',
                },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Retirer l'accès ({removableCount})
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Section 3 : Liste des utilisateurs */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          borderRadius: { xs: '12px', md: '16px' },
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid rgba(255, 255, 255, 0.10)',
          backdropFilter: 'blur(8px)',
          color: '#fff',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 2 }}
          onClick={() => toggleSection('users')}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Gestion des utilisateurs ({usersWithPermissions.length})
          </Typography>
          {expandedSections.users ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        <Collapse in={expandedSections.users}>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher un utilisateur (email, nom, prénom, rôle)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                borderRadius: '12px',
                backgroundColor: 'rgba(255,255,255,0.05)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.20)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.45)' },
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255,255,255,0.5)',
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                </InputAdornment>
              ),
            }}
          />

          {adminUsers.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'rgba(255,255,255,0.9)' }}>
                Administrateurs ({adminUsers.length})
              </Typography>
              <TableContainer sx={{ mb: { xs: 2, md: 4 }, overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        #
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        Nom
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        Email
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        Rôle
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        Boîtiers accessibles
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adminUsers.map((user, index) => (
                      <TableRow
                        key={user.id}
                        sx={{
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                        }}
                      >
                        <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.1)', fontWeight: 600 }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.1)' }}>
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.1)' }}>
                          {user.email}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                          <Chip
                            label="ADMIN"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(255,255,255,0.12)',
                              color: '#fff',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ borderColor: 'rgba(255,255,255,0.1)', cursor: 'pointer' }}
                          onClick={(event) => handleOpenPermissions(event, user)}
                        >
                          <Chip
                            label="Tous les boîtiers"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(255,255,255,0.12)',
                              color: '#fff',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {adminUsers.map((user, index) => (
                  <Paper
                    key={user.id}
                    sx={{
                      p: 1.5,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        #{index + 1}
                      </Typography>
                      <Chip
                        label="ADMIN"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.12)',
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {user.first_name} {user.last_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                      {user.email}
                    </Typography>
                    <Chip
                      label="Tous les boîtiers"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            </>
          )}

          {normalUsers.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'rgba(255,255,255,0.9)' }}>
                Utilisateurs ({normalUsers.length})
              </Typography>
              <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        #
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        Nom
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        Email
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        Rôle
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>
                        Boîtiers assignés
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {normalUsers.map((user, index) => (
                      <TableRow
                        key={user.id}
                        sx={{
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                        }}
                      >
                        <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.1)', fontWeight: 600 }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.1)' }}>
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.1)' }}>
                          {user.email}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                          <Chip
                            label="USER"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(255,255,255,0.12)',
                              color: '#fff',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ borderColor: 'rgba(255,255,255,0.1)', cursor: 'pointer' }}
                          onClick={(event) => handleOpenPermissions(event, user)}
                        >
                          {user.permissions && user.permissions.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {user.permissions.map((perm, idx) => (
                                <Tooltip
                                  key={idx}
                                  title={`${perm.table_name} - ${perm.device_id}`}
                                  arrow
                                >
                                  <Chip
                                    label={`${perm.device_id.slice(-4)} (${perm.table_name.slice(0, 3)})`}
                                    size="small"
                                    onDelete={(event) => {
                                      event.stopPropagation();
                                      handleDeletePermission(user.id, perm.table_name, perm.device_id);
                                    }}
                                    deleteIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                      backgroundColor: 'rgba(255,255,255,0.1)',
                                      color: '#fff',
                                      '& .MuiChip-deleteIcon': {
                                        color: 'rgba(255,255,255,0.7)',
                                        '&:hover': { color: '#fff' },
                                      },
                                    }}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}
                            >
                              Aucun boîtier assigné
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
                {normalUsers.map((user, index) => (
                  <Paper
                    key={user.id}
                    sx={{
                      p: 1.5,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        #{index + 1}
                      </Typography>
                      <Chip
                        label="USER"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.12)',
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {user.first_name} {user.last_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                      {user.email}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
                      Boîtiers assignés
                    </Typography>
                    {user.permissions && user.permissions.length > 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          overflowX: 'auto',
                          pb: 0.5,
                        }}
                      >
                        {user.permissions.map((perm, idx) => (
                          <Chip
                            key={idx}
                            label={`${perm.device_id.slice(-4)} (${perm.table_name.slice(0, 3)})`}
                            size="small"
                            onDelete={() =>
                              handleDeletePermission(user.id, perm.table_name, perm.device_id)
                            }
                            deleteIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              color: '#fff',
                              '& .MuiChip-deleteIcon': {
                                color: 'rgba(255,255,255,0.7)',
                                '&:hover': { color: '#fff' },
                              },
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                        Aucun boîtier assigné
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            </>
          )}

          {filteredUsers.length === 0 && (
            <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', py: 3 }}>
              Aucun utilisateur trouvé
            </Typography>
          )}
        </Collapse>
      </Paper>

      <Popover
        open={permissionsOpen}
        anchorEl={permissionsAnchorEl}
        onClose={handleClosePermissions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1b2e',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 2,
            p: 2,
            minWidth: 320,
            maxWidth: 480,
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Boîtiers assignés
        </Typography>
        {permissionsUser?.role === 'admin' ? (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Tous les boîtiers
          </Typography>
        ) : (
          <>
            {(permissionsUser?.permissions || []).length === 0 ? (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Aucun boîtier assigné
              </Typography>
            ) : (
              Object.entries(
                (permissionsUser?.permissions || []).reduce((acc, perm) => {
                  const key = perm.table_name;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(String(perm.device_id));
                  return acc;
                }, {})
              ).map(([tableName, deviceIds]) => (
                <Box key={tableName} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.75)', mb: 0.5 }}>
                    {tableName}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {deviceIds.map((id) => (
                      <Chip
                        key={id}
                        label={id}
                        size="small"
                        onDelete={() =>
                          handleDeletePermission(permissionsUser.id, tableName, id)
                        }
                        deleteIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: '#fff',
                          '& .MuiChip-deleteIcon': {
                            color: 'rgba(255,255,255,0.7)',
                            '&:hover': { color: '#fff' },
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))
            )}
          </>
        )}
      </Popover>
    </Box>
  );
}
